/** @jsxImportSource @emotion/react */

import { FormEvent, useState } from 'react';
import { CSSObject } from '@emotion/react';

import {
  PaymentMethod,
  StripeError,
  Stripe,
  StripeElementsOptions,
} from '@stripe/stripe-js';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import useOverlay from '../hooks/useOverlay';
import useLanguage from '../hooks/useLanguage';
import { IBaseOverlay } from '../contexts/overlayContext';
import Clickable from '../components/clickable';
import CheckBox from '../components/checkBox';
import useTheme from '../hooks/useTheme';
import environment from '../utils/environment';

export interface IPaymentSheetData {
  adjustPurchase: {
    revenue: string;
    currency: string;
  };
  attemptUrl: string;
  centesimalValue: number;
  currency: string;
  customerId: string;
  defaultBillingAddressCountry: string;
  defaultPaymentMethodId: string | null;
  ephemeralKeySecret: string;
  paymentMethodTypes: string[];
  paymentSheetButtonTitle: string;
  publishableKey: string;
  relayPayload: unknown;
}

export interface IStripeCheckoutOverlay extends IBaseOverlay {
  stripe: Stripe;
  stripeParameters: Partial<IPaymentSheetData> &
    Pick<IPaymentSheetData, 'centesimalValue' | 'currency'>;
  onPaymentMethod: (
    paymentMethod: PaymentMethod,
    shouldSaveCard: boolean
  ) => void;
  onError: (error: StripeError) => void;
}

interface ICheckoutForm {
  onPaymentMethod: (
    paymentMethod: PaymentMethod,
    shouldSaveCard: boolean
  ) => void;
  onError: (error: StripeError) => void;
  payBtnText: string | undefined;
}

const CheckoutForm = ({
  onPaymentMethod,
  onError,
  payBtnText,
}: ICheckoutForm) => {
  const stripe = useStripe();
  const elements = useElements();

  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeValid, setStripeValid] = useState(false);
  const [shouldSaveCard, setShouldSaveCard] = useState(false);

  const { font, color } = useTheme();
  const overlay = useOverlay();
  const language = useLanguage();

  const FORM_STYLE: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    backgroundColor: color.LOCAL_WHITE,
    padding: '30px 20px 10px',
    borderRadius: 7,

    '.StripeElement': {
      boxSizing: 'border-box',
      paddingTop: '20px',
      paddingBottom: '10px',
      borderTop: `1px solid ${color.DIVIDER_LINE}`,
    },
  };

  const HEADER_STYLE: CSSObject = {
    fontSize: 17,
    fontFamily: font.FAMILY_BOLD,
    lineHeight: '21px',
    marginBottom: 20,
  };

  const CANCEL_BUTTON_STYLE: CSSObject = {
    backgroundColor: color.LOCAL_WHITE,
    color: color.PRIMARY,
    '&[active], :disabled': {
      backgroundColor: color.LOCAL_WHITE,
    },
  };

  const SAVE_CARD_STYLE: CSSObject = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '25px',
    columnGap: '8px',
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setStripeBusy(true);

    try {
      const { error: submitError } = await elements.submit();

      if (submitError) {
        onError(submitError as StripeError);
        setStripeBusy(false);
        return;
      }

      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          elements,
        });

      if (paymentMethodError) {
        onError(paymentMethodError);
        setStripeBusy(false);
        return;
      }

      if (paymentMethod) {
        onPaymentMethod(paymentMethod, shouldSaveCard);
        overlay.dismiss();
      }
    } catch (error) {
      onError(error as StripeError);

      setStripeBusy(false);
    }
  };

  const appMarket = environment.COUNTRY;

  return (
    <form onSubmit={handleSubmit} css={FORM_STYLE}>
      {/* Dialog header */}
      <h2 css={HEADER_STYLE}>{language.get('pay_with_card')}</h2>
      <PaymentElement
        id='payment-element'
        options={{
          defaultValues: {
            billingDetails: {
              address: {
                country: appMarket ?? undefined, // let Stripe fallback if appMarket is falsy,
              },
            },
          },
          // Wallets now have their own standalone StripeExpressCheckoutPart button
          // rendered directly on the screen - this sheet is card-only.
          wallets: { applePay: 'never', googlePay: 'never' },
          // Both pinned rather than left to Stripe's defaults, which follow the loaded
          // Stripe.js release train and so can change under us on an SDK bump. "auto"
          // would not pin anything - Stripe still picks tabs vs accordion itself - and
          // an undefined defaultCollapsed lets Stripe's conversion heuristic decide.
          // Together these keep the card fields on screen instead of behind a collapsed
          // accordion row the patient has to tap open first.
          layout: { type: 'tabs', defaultCollapsed: false },
        }}
        onChange={(event) => setStripeValid(event.complete)}
      />

      {/* Save card checkbox */}
      <Clickable renderAs='label' defaultHandler css={SAVE_CARD_STYLE}>
        <CheckBox
          checked={shouldSaveCard}
          disabled={stripeBusy}
          onChange={(e) => {
            setShouldSaveCard(e.target.checked);
          }}
        />
        {language.get('save_card_details')}
      </Clickable>

      {/* Pay button */}
      <Clickable
        styleAs='button'
        defaultHandler
        disabled={stripeBusy || !stripeValid}
      >
        {payBtnText ?? language.get('pay')}
      </Clickable>

      {/* Cancel button */}
      <Clickable
        type='button'
        styleAs='button'
        disabled={stripeBusy}
        css={CANCEL_BUTTON_STYLE}
        onClick={() => {
          overlay.dismiss();
        }}
      >
        {language.get('cancel')}
      </Clickable>
    </form>
  );
};

const StripeCheckoutOverlay = ({
  stripe,
  stripeParameters,
  onPaymentMethod,
  onError,
}: IStripeCheckoutOverlay) => {
  const overlay = useOverlay();

  const options: StripeElementsOptions = {
    mode: 'payment',
    currency: stripeParameters.currency.toLowerCase(),
    amount: stripeParameters.centesimalValue,
    paymentMethodCreation: 'manual',
    paymentMethodTypes: stripeParameters.paymentMethodTypes,
  };

  const payBtnText = stripeParameters.paymentSheetButtonTitle;

  return (
    <div>
      <Elements stripe={stripe} options={options}>
        <CheckoutForm
          onPaymentMethod={(paymentMethod, shouldSaveCard) => {
            overlay.dismiss();
            onPaymentMethod(paymentMethod, shouldSaveCard);
          }}
          onError={(error) => {
            overlay.dismiss();
            onError(error);
          }}
          payBtnText={payBtnText}
        />
      </Elements>
    </div>
  );
};

export default StripeCheckoutOverlay;
